import { isManagerPlus, readUserPosition } from "../../access/positions";
import { useAccess } from "../../access/AccessContext";
// Network-access management. Embedded as a section inside admin-settings
// so it lives next to the other warehouse-wide configuration. Four
// sub-blocks: add form, active allow-list, login activity (every IP
// that hit /login/user with success/failure counts), blocked-attempts
// (post-enforcement). Compact layout to fit alongside the other
// settings sections.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Trash2, Search, Loader2, X, Unlock } from "lucide-react";
import { toast } from "react-toastify";
import { API_BASE } from "../../api/base";
import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";
import "../../styles/admin-ip-allowlist.css";

interface AllowlistEntry {
    _id: string;
    cidr: string;
    label: string;
    enabled: boolean;
    createdBy: number | null;
    createdAt: string;
    updatedAt: string;
}

interface BlockedAttempt {
    _id: string;
    ip: string;
    count: number;
    firstSeenAt: string;
    lastSeenAt: string;
    lastPath: string | null;
    lastMethod: string | null;
    lastUserAgent: string | null;
}

interface LoginAttempt {
    _id: string;
    ip: string;
    successCount: number;
    failureCount: number;
    firstSeenAt: string;
    lastSeenAt: string;
    lastStatus: "success" | "failure" | null;
    lastColour: string | null;
    lastUserName: string | null;
    lastUid: number | null;
}

interface ColourLockout {
    _id: string;
    colour: string;
    consecutiveFailures: number;
    distinctFailureIps: number;
    lockedUntil: string | null;
    lastFailureAt: string | null;
    lastFailureIp: string | null;
    currentlyLocked: boolean;
}

const fmtDateTime = (iso: string) => {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

const IpAllowlistAdmin = () => {
    const { t } = useTranslation();

    const [entries, setEntries] = useState<AllowlistEntry[]>([]);
    const [blocked, setBlocked] = useState<BlockedAttempt[]>([]);
    const [logins, setLogins] = useState<LoginAttempt[]>([]);
    const [loadingEntries, setLoadingEntries] = useState(true);
    const [loadingBlocked, setLoadingBlocked] = useState(true);
    const [loadingLogins, setLoadingLogins] = useState(true);

    const [newCidr, setNewCidr] = useState("");
    const [newLabel, setNewLabel] = useState("");
    const [adding, setAdding] = useState(false);
    const [whoamiLoading, setWhoamiLoading] = useState(false);

    const [pendingId, setPendingId] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [loginSearch, setLoginSearch] = useState("");

    // Inline label editing on Active allow-list rows. editingLabelId
    // holds the _id of the row currently in edit mode (null = none).
    // labelDraft is the in-progress text. savingLabelId blocks the
    // input while the PATCH is in flight.
    const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
    const [labelDraft, setLabelDraft] = useState("");
    const [savingLabelId, setSavingLabelId] = useState<string | null>(null);

    const [lockouts, setLockouts] = useState<ColourLockout[]>([]);
    const [loadingLockouts, setLoadingLockouts] = useState(true);
    const [unlockingColour, setUnlockingColour] = useState<string | null>(null);

    // Unlock is a fixed rule on the BE: Manager or Admin, holding Colour lockouts write. The
    // screen used to show it to Admin only, hiding a button Managers were allowed to use (2026-10-02).
    const { authoritative: accessAuthoritative, canWrite } = useAccess();
    const isSuperAdmin = isManagerPlus(readUserPosition()) && (!accessAuthoritative || canWrite("admin.users.lockouts"));

    const allowedCidrs = useMemo(
        () => new Set(entries.filter(e => e.enabled).map(e => e.cidr)),
        [entries]
    );

    const loadEntries = useCallback(async () => {
        setLoadingEntries(true);
        try {
            const res = await apiFetch(`/admin/ip-allowlist`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: AllowlistEntry[] = await res.json();
            setEntries(data);
        } catch {
            toast.error(t("server_error"), { toastId: "ip-allowlist-load" });
        } finally {
            setLoadingEntries(false);
        }
    }, [t]);

    const loadBlocked = useCallback(async (q: string) => {
        setLoadingBlocked(true);
        try {
            const url = new URL(`${API_BASE}/admin/ip-allowlist/blocked`);
            if (q) url.searchParams.set("q", q);
            const res = await apiFetch(url.toString());
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: BlockedAttempt[] = await res.json();
            setBlocked(data);
        } catch {
            toast.error(t("server_error"), { toastId: "ip-blocked-load" });
        } finally {
            setLoadingBlocked(false);
        }
    }, [t]);

    const loadLogins = useCallback(async (q: string) => {
        setLoadingLogins(true);
        try {
            const url = new URL(`${API_BASE}/admin/ip-allowlist/login-attempts`);
            if (q) url.searchParams.set("q", q);
            const res = await apiFetch(url.toString());
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: LoginAttempt[] = await res.json();
            setLogins(data);
        } catch {
            toast.error(t("server_error"), { toastId: "ip-logins-load" });
        } finally {
            setLoadingLogins(false);
        }
    }, [t]);

    useEffect(() => { loadEntries(); }, [loadEntries]);

    useEffect(() => {
        const handle = window.setTimeout(() => loadBlocked(search.trim()), 250);
        return () => window.clearTimeout(handle);
    }, [search, loadBlocked]);

    useEffect(() => {
        const handle = window.setTimeout(() => loadLogins(loginSearch.trim()), 250);
        return () => window.clearTimeout(handle);
    }, [loginSearch, loadLogins]);

    const loadLockouts = useCallback(async () => {
        setLoadingLockouts(true);
        try {
            const res = await apiFetch(`/admin/colour-lockouts`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: ColourLockout[] = await res.json();
            setLockouts(data);
        } catch {
            toast.error(t("server_error"), { toastId: "colour-lockouts-load" });
        } finally {
            setLoadingLockouts(false);
        }
    }, [t]);

    useEffect(() => { loadLockouts(); }, [loadLockouts]);

    const unlockColour = async (colour: string) => {
        setUnlockingColour(colour);
        try {
            const res = await apiFetch(
                `/admin/colour-lockouts/${encodeURIComponent(colour)}`,
                { method: "DELETE" }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success(t("colour_lockout_unlocked", { colour }), { toastId: "colour-lockout-unlocked" });
            await loadLockouts();
        } catch {
            toast.error(t("server_error"), { toastId: "colour-lockout-unlock" });
        } finally {
            setUnlockingColour(null);
        }
    };

    // Only show currently-locked colours by default; expired-but-uncleared
    // rows are noisy and rarely actionable. (The BE keeps them around so
    // the audit trail survives if we ever add a "history" view.)
    const activeLockouts = useMemo(
        () => lockouts.filter(l => l.currentlyLocked),
        [lockouts]
    );

    const useMyIp = async () => {
        setWhoamiLoading(true);
        try {
            const res = await apiFetch(`/admin/ip-allowlist/whoami`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: { ip: string } = await res.json();
            setNewCidr(`${data.ip}/32`);
            if (!newLabel) setNewLabel(t("ip_allowlist_my_network_label"));
        } catch {
            toast.error(t("server_error"), { toastId: "ip-allowlist-whoami" });
        } finally {
            setWhoamiLoading(false);
        }
    };

    const submitAdd = async (cidr: string, label: string) => {
        const cleanCidr = cidr.trim();
        const cleanLabel = label.trim();
        if (!cleanCidr || !cleanLabel) {
            toast.error(t("ip_allowlist_form_required"), { toastId: "ip-allowlist-required" });
            return false;
        }
        setAdding(true);
        try {
            const res = await apiFetch(`/admin/ip-allowlist`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ cidr: cleanCidr, label: cleanLabel })
            });
            const data = await res.json();
            if (!res.ok) {
                toast.error(data?.message || t("server_error"), { toastId: "ip-allowlist-add" });
                return false;
            }
            toast.success(t("ip_allowlist_added"), { toastId: "ip-allowlist-added" });
            await loadEntries();
            return true;
        } catch {
            toast.error(t("server_error"), { toastId: "ip-allowlist-add" });
            return false;
        } finally {
            setAdding(false);
        }
    };

    const onAddSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const ok = await submitAdd(newCidr, newLabel);
        if (ok) {
            setNewCidr("");
            setNewLabel("");
        }
    };

    const toggleEnabled = async (entry: AllowlistEntry) => {
        const prev = entries;
        const nextEnabled = !entry.enabled;
        setEntries(curr => curr.map(e => e._id === entry._id ? { ...e, enabled: nextEnabled } : e));
        setPendingId(entry._id);
        try {
            const res = await apiFetch(`/admin/ip-allowlist/${entry._id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ enabled: nextEnabled })
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: AllowlistEntry = await res.json();
            setEntries(curr => curr.map(e => e._id === entry._id ? updated : e));
        } catch {
            setEntries(prev);
            toast.error(t("server_error"), { toastId: "ip-allowlist-toggle" });
        } finally {
            setPendingId(null);
        }
    };

    const startEditLabel = (entry: AllowlistEntry) => {
        if (savingLabelId) return;
        setEditingLabelId(entry._id);
        setLabelDraft(entry.label);
    };

    const cancelEditLabel = () => {
        setEditingLabelId(null);
        setLabelDraft("");
    };

    const commitEditLabel = async (entry: AllowlistEntry) => {
        const next = labelDraft.trim();
        // No-op: same value or empty (allow-list rows must have a label).
        if (next === entry.label) {
            cancelEditLabel();
            return;
        }
        if (!next) {
            toast.error(t("ip_allowlist_form_required"), { toastId: "ip-allowlist-label-required" });
            return;
        }
        const prev = entries;
        // Optimistic update so the row reads "saved" instantly; rolls
        // back on PATCH failure.
        setEntries(curr => curr.map(e => e._id === entry._id ? { ...e, label: next } : e));
        setSavingLabelId(entry._id);
        try {
            const res = await apiFetch(`/admin/ip-allowlist/${entry._id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ label: next })
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: AllowlistEntry = await res.json();
            setEntries(curr => curr.map(e => e._id === entry._id ? updated : e));
        } catch {
            setEntries(prev);
            toast.error(t("server_error"), { toastId: "ip-allowlist-label-save" });
        } finally {
            setSavingLabelId(null);
            setEditingLabelId(null);
            setLabelDraft("");
        }
    };

    const deleteEntry = async (entry: AllowlistEntry) => {
        if (!window.confirm(t("ip_allowlist_confirm_delete", { cidr: entry.cidr }))) return;
        setPendingId(entry._id);
        try {
            const res = await apiFetch(`/admin/ip-allowlist/${entry._id}`, {
                method: "DELETE",
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setEntries(curr => curr.filter(e => e._id !== entry._id));
            toast.success(t("ip_allowlist_deleted"), { toastId: "ip-allowlist-deleted" });
        } catch {
            toast.error(t("server_error"), { toastId: "ip-allowlist-delete" });
        } finally {
            setPendingId(null);
        }
    };

    const dismissBlocked = async (ip: string) => {
        try {
            const res = await apiFetch(
                `/admin/ip-allowlist/blocked/${encodeURIComponent(ip)}`,
                { method: "DELETE" }
            );
            if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
            setBlocked(curr => curr.filter(b => b.ip !== ip));
        } catch {
            toast.error(t("server_error"), { toastId: "ip-blocked-dismiss" });
        }
    };

    const quickAddBlocked = async (b: BlockedAttempt) => {
        const cidr = `${b.ip}/32`;
        const label = t("ip_allowlist_added_from_blocked", { ip: b.ip });
        const ok = await submitAdd(cidr, label);
        if (ok) await dismissBlocked(b.ip);
    };

    const dismissLogin = async (ip: string) => {
        try {
            const res = await apiFetch(
                `/admin/ip-allowlist/login-attempts/${encodeURIComponent(ip)}`,
                { method: "DELETE" }
            );
            if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
            setLogins(curr => curr.filter(l => l.ip !== ip));
        } catch {
            toast.error(t("server_error"), { toastId: "ip-logins-dismiss" });
        }
    };

    const quickAddLogin = async (l: LoginAttempt) => {
        const cidr = `${l.ip}/32`;
        // If we know who logged in from this IP, label with their name;
        // otherwise label with the IP itself so the row is at least
        // attributable.
        const label = l.lastUserName
            ? t("ip_allowlist_added_from_login_named", { name: l.lastUserName })
            : t("ip_allowlist_added_from_login", { ip: l.ip });
        const ok = await submitAdd(cidr, label);
        if (ok) await dismissLogin(l.ip);
    };

    return (
        <div className="ip-allowlist-admin">
            {/* Add form — single inline row on desktop. */}
            <h3 className="admin-settings-subsection-title">{t("ip_allowlist_add_title")}</h3>
            <p className="admin-settings-section-hint">{t("ip_allowlist_add_hint")}</p>
            <form className="ip-allowlist-add-form" onSubmit={onAddSubmit}>
                <input
                    className="ip-allowlist-input ip-allowlist-input--cidr"
                    type="text"
                    placeholder={t("ip_allowlist_cidr_placeholder")}
                    value={newCidr}
                    onChange={e => setNewCidr(e.target.value)}
                    disabled={adding}
                    autoComplete="off"
                    spellCheck={false}
                    aria-label={t("ip_allowlist_cidr_label")}
                />
                <input
                    className="ip-allowlist-input"
                    type="text"
                    placeholder={t("ip_allowlist_label_placeholder")}
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    disabled={adding}
                    maxLength={100}
                    aria-label={t("ip_allowlist_label_label")}
                />
                <button
                    type="button"
                    className="ip-allowlist-btn ip-allowlist-btn--ghost"
                    onClick={useMyIp}
                    disabled={adding || whoamiLoading}
                >
                    {whoamiLoading
                        ? <Loader2 size={14} className="ip-allowlist-spinner" />
                        : t("ip_allowlist_use_my_ip")}
                </button>
                <button
                    type="submit"
                    className="ip-allowlist-btn ip-allowlist-btn--primary"
                    disabled={adding || !newCidr.trim() || !newLabel.trim()}
                >
                    {adding ? t("saving") : t("ip_allowlist_add_button")}
                </button>
            </form>

            {/* Active allow-list */}
            <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                {t("ip_allowlist_active_title")}
                {!loadingEntries && (
                    <span className="ip-allowlist-count">{entries.length}</span>
                )}
            </h3>
            {loadingEntries ? (
                <div className="ip-allowlist-loading-line">{t("loading")}…</div>
            ) : entries.length === 0 ? (
                <div className="ip-allowlist-empty ip-allowlist-empty--warn">
                    {t("ip_allowlist_empty_warning")}
                </div>
            ) : (
                <div className="ip-allowlist-table-wrap">
                    <table className="ip-allowlist-table">
                        <thead>
                            <tr>
                                <th>{t("ip_allowlist_col_cidr")}</th>
                                <th>{t("ip_allowlist_col_label")}</th>
                                <th>{t("ip_allowlist_col_enabled")}</th>
                                <th>{t("ip_allowlist_col_created")}</th>
                                <th aria-label={t("delete")} />
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map(e => (
                                <tr key={e._id} className={e.enabled ? "" : "ip-allowlist-row--disabled"}>
                                    <td className="ip-allowlist-mono">{e.cidr}</td>
                                    <td className="ip-allowlist-label-cell">
                                        {editingLabelId === e._id ? (
                                            <input
                                                className="ip-allowlist-input ip-allowlist-label-input"
                                                type="text"
                                                value={labelDraft}
                                                autoFocus
                                                disabled={savingLabelId === e._id}
                                                maxLength={100}
                                                onChange={ev => setLabelDraft(ev.target.value)}
                                                onFocus={ev => ev.target.select()}
                                                onBlur={() => commitEditLabel(e)}
                                                onKeyDown={ev => {
                                                    if (ev.key === "Enter") {
                                                        ev.preventDefault();
                                                        commitEditLabel(e);
                                                    } else if (ev.key === "Escape") {
                                                        ev.preventDefault();
                                                        cancelEditLabel();
                                                    }
                                                }}
                                                aria-label={t("ip_allowlist_label_label")}
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                className="ip-allowlist-label-trigger"
                                                onClick={() => startEditLabel(e)}
                                                disabled={!!savingLabelId}
                                                title={t("ip_allowlist_edit_label_hint")}
                                            >
                                                {e.label}
                                            </button>
                                        )}
                                    </td>
                                    <td>
                                        <label className="ip-allowlist-toggle">
                                            <input
                                                type="checkbox"
                                                checked={e.enabled}
                                                disabled={pendingId === e._id}
                                                onChange={() => toggleEnabled(e)}
                                            />
                                            <span className="ip-allowlist-slider" />
                                        </label>
                                    </td>
                                    <td className="ip-allowlist-mono ip-allowlist-dim">
                                        {fmtDateTime(e.createdAt)}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="ip-allowlist-icon-btn ip-allowlist-icon-btn--danger"
                                            onClick={() => deleteEntry(e)}
                                            disabled={pendingId === e._id}
                                            aria-label={t("delete")}
                                            title={t("delete")}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Locked colours — only renders when at least one colour is
                currently in lockout. Lockouts auto-clear after 15 min,
                so most of the time this section is invisible; when it
                appears, it's actionable. */}
            {!loadingLockouts && activeLockouts.length > 0 && (
                <>
                    <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                        {t("colour_lockout_title")}
                        <span className="ip-allowlist-count">{activeLockouts.length}</span>
                    </h3>
                    <p className="admin-settings-section-hint">{t("colour_lockout_hint")}</p>
                    <div className="ip-allowlist-table-wrap">
                        <table className="ip-allowlist-table">
                            <thead>
                                <tr>
                                    <th>{t("colour_lockout_col_colour")}</th>
                                    <th>{t("colour_lockout_col_failures")}</th>
                                    <th>{t("colour_lockout_col_distinct_ips")}</th>
                                    <th>{t("colour_lockout_col_locked_until")}</th>
                                    <th>{t("colour_lockout_col_last_ip")}</th>
                                    {isSuperAdmin && <th aria-label={t("colour_lockout_unlock")} />}
                                </tr>
                            </thead>
                            <tbody>
                                {activeLockouts.map(l => (
                                    <tr key={l._id}>
                                        <td>{l.colour}</td>
                                        <td>{l.consecutiveFailures}</td>
                                        <td>{l.distinctFailureIps}</td>
                                        <td className="ip-allowlist-mono ip-allowlist-dim">
                                            {l.lockedUntil ? fmtDateTime(l.lockedUntil) : "—"}
                                        </td>
                                        <td className="ip-allowlist-mono ip-allowlist-dim">
                                            {l.lastFailureIp ?? "—"}
                                        </td>
                                        {isSuperAdmin && (
                                            <td>
                                                <button
                                                    type="button"
                                                    className="ip-allowlist-icon-btn ip-allowlist-icon-btn--success"
                                                    onClick={() => unlockColour(l.colour)}
                                                    disabled={unlockingColour === l.colour}
                                                    title={t("colour_lockout_unlock")}
                                                    aria-label={t("colour_lockout_unlock")}
                                                >
                                                    <Unlock size={14} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Login activity — every IP that's hit /login/user, with
                success/failure counts and a tick to add legitimate IPs
                to the allow-list before flipping enforcement on. */}
            <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                {t("ip_allowlist_logins_title")}
                {!loadingLogins && (
                    <span className="ip-allowlist-count">{logins.length}</span>
                )}
            </h3>
            <p className="admin-settings-section-hint">{t("ip_allowlist_logins_hint")}</p>

            <div className="ip-allowlist-search">
                <Search size={14} className="ip-allowlist-search-icon" />
                <input
                    className="ip-allowlist-input"
                    type="search"
                    placeholder={t("ip_allowlist_search_placeholder")}
                    value={loginSearch}
                    onChange={e => setLoginSearch(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                />
            </div>

            {loadingLogins ? (
                <div className="ip-allowlist-loading-line">{t("loading")}…</div>
            ) : logins.length === 0 ? (
                <div className="ip-allowlist-empty">{t("ip_allowlist_logins_empty")}</div>
            ) : (
                <div className="ip-allowlist-table-wrap">
                    <table className="ip-allowlist-table">
                        <thead>
                            <tr>
                                <th>{t("ip_allowlist_col_ip")}</th>
                                <th>{t("ip_allowlist_col_user")}</th>
                                <th>{t("ip_allowlist_col_status")}</th>
                                <th>{t("ip_allowlist_col_success")}</th>
                                <th>{t("ip_allowlist_col_failure")}</th>
                                <th>{t("ip_allowlist_col_first_seen")}</th>
                                <th>{t("ip_allowlist_col_last_seen")}</th>
                                <th aria-label={t("ip_allowlist_quick_add")} />
                                <th aria-label={t("ip_allowlist_dismiss")} />
                            </tr>
                        </thead>
                        <tbody>
                            {logins.map(l => {
                                const alreadyAllowed = allowedCidrs.has(`${l.ip}/32`);
                                const userLabel = l.lastUserName
                                    ? `${l.lastUserName}${l.lastColour ? ` (${l.lastColour})` : ""}`
                                    : (l.lastColour ?? "—");
                                return (
                                    <tr key={l._id}>
                                        <td className="ip-allowlist-mono">{l.ip}</td>
                                        <td>{userLabel}</td>
                                        <td>
                                            {l.lastStatus === "success" && (
                                                <span className="ip-allowlist-status ip-allowlist-status--success">
                                                    {t("ip_allowlist_status_success")}
                                                </span>
                                            )}
                                            {l.lastStatus === "failure" && (
                                                <span className="ip-allowlist-status ip-allowlist-status--failure">
                                                    {t("ip_allowlist_status_failure")}
                                                </span>
                                            )}
                                            {!l.lastStatus && (
                                                <span className="ip-allowlist-dim">—</span>
                                            )}
                                        </td>
                                        <td>{l.successCount}</td>
                                        <td>{l.failureCount}</td>
                                        <td className="ip-allowlist-mono ip-allowlist-dim">
                                            {fmtDateTime(l.firstSeenAt)}
                                        </td>
                                        <td className="ip-allowlist-mono ip-allowlist-dim">
                                            {fmtDateTime(l.lastSeenAt)}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="ip-allowlist-icon-btn ip-allowlist-icon-btn--success"
                                                onClick={() => quickAddLogin(l)}
                                                disabled={adding || alreadyAllowed}
                                                title={alreadyAllowed
                                                    ? t("ip_allowlist_already_allowed")
                                                    : t("ip_allowlist_quick_add")}
                                                aria-label={t("ip_allowlist_quick_add")}
                                            >
                                                <Check size={14} />
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="ip-allowlist-icon-btn"
                                                onClick={() => dismissLogin(l.ip)}
                                                title={t("ip_allowlist_dismiss")}
                                                aria-label={t("ip_allowlist_dismiss")}
                                            >
                                                <X size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Blocked attempts */}
            <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                {t("ip_allowlist_blocked_title")}
                {!loadingBlocked && (
                    <span className="ip-allowlist-count">{blocked.length}</span>
                )}
            </h3>
            <p className="admin-settings-section-hint">{t("ip_allowlist_blocked_hint")}</p>

            <div className="ip-allowlist-search">
                <Search size={14} className="ip-allowlist-search-icon" />
                <input
                    className="ip-allowlist-input"
                    type="search"
                    placeholder={t("ip_allowlist_search_placeholder")}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                />
            </div>

            {loadingBlocked ? (
                <div className="ip-allowlist-loading-line">{t("loading")}…</div>
            ) : blocked.length === 0 ? (
                <div className="ip-allowlist-empty">{t("ip_allowlist_blocked_empty")}</div>
            ) : (
                <div className="ip-allowlist-table-wrap">
                    <table className="ip-allowlist-table">
                        <thead>
                            <tr>
                                <th>{t("ip_allowlist_col_ip")}</th>
                                <th>{t("ip_allowlist_col_attempts")}</th>
                                <th>{t("ip_allowlist_col_first_seen")}</th>
                                <th>{t("ip_allowlist_col_last_seen")}</th>
                                <th>{t("ip_allowlist_col_last_request")}</th>
                                <th aria-label={t("ip_allowlist_quick_add")} />
                                <th aria-label={t("ip_allowlist_dismiss")} />
                            </tr>
                        </thead>
                        <tbody>
                            {blocked.map(b => {
                                const alreadyAllowed = allowedCidrs.has(`${b.ip}/32`);
                                return (
                                    <tr key={b._id}>
                                        <td className="ip-allowlist-mono">{b.ip}</td>
                                        <td>{b.count}</td>
                                        <td className="ip-allowlist-mono ip-allowlist-dim">
                                            {fmtDateTime(b.firstSeenAt)}
                                        </td>
                                        <td className="ip-allowlist-mono ip-allowlist-dim">
                                            {fmtDateTime(b.lastSeenAt)}
                                        </td>
                                        <td className="ip-allowlist-mono ip-allowlist-dim ip-allowlist-cell-truncate">
                                            {b.lastMethod ?? "—"} {b.lastPath ?? ""}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="ip-allowlist-icon-btn ip-allowlist-icon-btn--success"
                                                onClick={() => quickAddBlocked(b)}
                                                disabled={adding || alreadyAllowed}
                                                title={alreadyAllowed
                                                    ? t("ip_allowlist_already_allowed")
                                                    : t("ip_allowlist_quick_add")}
                                                aria-label={t("ip_allowlist_quick_add")}
                                            >
                                                <Check size={14} />
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="ip-allowlist-icon-btn"
                                                onClick={() => dismissBlocked(b.ip)}
                                                title={t("ip_allowlist_dismiss")}
                                                aria-label={t("ip_allowlist_dismiss")}
                                            >
                                                <X size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default IpAllowlistAdmin;
